import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { useCanPromotePartnerProgram } from '@/components/PartnerProgram/useCanPromotePartnerProgram';
import { useSettings } from '@/hooks/state/useSettings';

/**
 * Users who signed up before this date never see the partner-program popup —
 * they're likely to have been served earlier referral pop-ups already
 * (DEV-6570).
 */
export const PARTNER_PROGRAM_POPUP_RELEASE_DATE = new Date(
  '2026-09-30T00:00:00Z'
);

// Every marker is keyed by user id so someone else logging in on the same
// device doesn't inherit another account's eligibility.
/** Signup timestamp (ms), persisted from the signup session (localStorage). */
const getSignupAtKey = (userId: string) => `partnerPopupSignupAt:${userId}`;
/** Current session since signup, shared by every tab (localStorage). */
const getSessionKey = (userId: string) => `partnerPopupSession:${userId}`;
/** Set once the popup has been shown — it only ever shows once (localStorage). */
const getShownKey = (userId: string) => `partnerPopupShown:${userId}`;

const MIN_SESSION_COUNT = 3;
/** 5 minutes */
const MIN_TIME_ON_SITE_MS = 300_000;
/** 30 minutes without a visible tab ends the session */
const SESSION_INACTIVITY_TIMEOUT_MS = 1_800_000;
const ACTIVITY_CHECK_INTERVAL_MS = 15_000;

const popupSessionSchema = z.object({
  /** 1-based session number since signup */
  count: z.number(),
  /** Time with a visible tab this session */
  activeMs: z.number(),
  lastActiveAt: z.number(),
});

type PopupSession = z.infer<typeof popupSessionSchema>;

type StorageName = 'localStorage' | 'sessionStorage';

// Storage can be blocked or full; failures read as missing and writes no-op.
const getItem = (storage: StorageName, key: string): string | null => {
  try {
    return window[storage].getItem(key);
  } catch {
    return null;
  }
};

const setItem = (storage: StorageName, key: string, value: string) => {
  try {
    window[storage].setItem(key, value);
  } catch {
    // Ignored: the popup just won't become eligible
  }
};

const readNumber = (storage: StorageName, key: string): number | null => {
  const raw = getItem(storage, key);
  const value = raw === null ? Number.NaN : Number.parseInt(raw, 10);

  return Number.isNaN(value) ? null : value;
};

const readSession = (userId: string): PopupSession | null => {
  try {
    const result = popupSessionSchema.safeParse(
      JSON.parse(getItem('localStorage', getSessionKey(userId)) ?? '')
    );

    return result.success ? result.data : null;
  } catch {
    return null;
  }
};

const writeSession = (userId: string, session: PopupSession) => {
  setItem('localStorage', getSessionKey(userId), JSON.stringify(session));
};

/** Extends the current session, or starts the next one once it has lapsed. */
const touchSession = (userId: string, now: number): PopupSession => {
  const session = readSession(userId);

  if (session && now - session.lastActiveAt < SESSION_INACTIVITY_TIMEOUT_MS) {
    const extended = {
      ...session,
      // Capped at one heartbeat so time spent hidden isn't counted
      activeMs:
        session.activeMs +
        Math.min(now - session.lastActiveAt, ACTIVITY_CHECK_INTERVAL_MS),
      lastActiveAt: now,
    };
    writeSession(userId, extended);

    return extended;
  }

  const next = {
    count: (session?.count ?? 0) + 1,
    activeMs: 0,
    lastActiveAt: now,
  };
  writeSession(userId, next);

  return next;
};

/**
 * Targeting for the partner-program floating widget (DEV-6570): only
 * non-partners who signed up after release, in their 3rd (or later) session, after 5+
 * minutes with a visible tab that session, and at most once ever.
 *
 * The signup marker piggybacks on the `signUpTime` sessionStorage value the
 * auth flows write, persisted here so it survives beyond the signup session;
 * users from before release never get the marker, so they're excluded.
 * Sessions are only tracked from signup onwards, in localStorage so every
 * tab shares one session, which ends after 30 minutes with no visible tab.
 */
export const usePartnerProgramPopup = () => {
  const [session, setSession] = useState<PopupSession | null>(null);
  // Read when the user changes, not when it's written, so marking the popup
  // shown doesn't yank one that's on screen when the user navigates.
  const [alreadyShown, setAlreadyShown] = useState(false);
  const [signedUpAfterRelease, setSignedUpAfterRelease] = useState(false);

  const { settings } = useSettings();
  const rawUserId = settings?.user?.id;
  const userId = rawUserId === undefined ? null : String(rawUserId);
  const canPromotePartnerProgram = useCanPromotePartnerProgram();

  // Re-runs on every user change: signup happens in a dialog without a page
  // load, and a different user may log in on the same device.
  useEffect(() => {
    setSession(null);
    setSignedUpAfterRelease(false);
    setAlreadyShown(false);

    if (userId === null) {
      return;
    }

    setAlreadyShown(getItem('localStorage', getShownKey(userId)) !== null);

    const signUpTime = readNumber('sessionStorage', 'signUpTime');

    if (
      signUpTime !== null &&
      getItem('localStorage', getSignupAtKey(userId)) === null
    ) {
      setItem('localStorage', getSignupAtKey(userId), String(signUpTime));
      const now = Date.now();
      writeSession(userId, { count: 1, activeMs: 0, lastActiveAt: now });
    }

    const signupAt = readNumber('localStorage', getSignupAtKey(userId));
    setSignedUpAfterRelease(
      signupAt !== null &&
        signupAt >= PARTNER_PROGRAM_POPUP_RELEASE_DATE.getTime()
    );

    if (signupAt === null) {
      return;
    }

    const sync = () => {
      setSession(
        document.visibilityState === 'hidden'
          ? readSession(userId)
          : touchSession(userId, Date.now())
      );
    };

    sync();
    const intervalId = setInterval(sync, ACTIVITY_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', sync);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', sync);
    };
  }, [userId]);

  const markShown = useCallback(() => {
    if (userId !== null) {
      setItem('localStorage', getShownKey(userId), String(Date.now()));
    }
  }, [userId]);

  const hasSpentMinimumTime =
    session !== null && session.activeMs >= MIN_TIME_ON_SITE_MS;

  return {
    isEligible:
      canPromotePartnerProgram &&
      signedUpAfterRelease &&
      !alreadyShown &&
      (session?.count ?? 0) >= MIN_SESSION_COUNT &&
      hasSpentMinimumTime,
    markShown,
  };
};
